import { ChatMessage, ChatCompletionResponse } from './api.types';
import { sendChatRequest, fetchAvailableTools, createSession } from './api';
import { useSession } from './components/SessionDropdown';

type MessageMeta = {
  timestamp?: string;
  duration?: number;
  isLiked?: boolean;
  isDisliked?: boolean;
  role?: string;
};

export type ChatFlowMessage = {
  id: string;
  type: 'system' | 'question';
  content: string;
  field?: string;
  suggestions?: string[];
  isFinished?: boolean;
} & MessageMeta;

export type UserMessage = {
  id: string;
  type: 'user';
  content: string;
  file?: {
    name: string;
    type: string;
    size: string;
  };
} & MessageMeta;

export type HelpMessage = {
  id: string;
  type: 'help';
  content: string;
  suggestions: string[];
} & MessageMeta;

export type ProceedMessage = {
  id: string;
  type: 'system';
  content: string;
} & MessageMeta;

export type ErrorMessage = {
  id: string;
  type: 'error';
  content: string;
  errorCode?: string;
} & MessageMeta;

export type Message = ChatFlowMessage | UserMessage | HelpMessage | ProceedMessage | ErrorMessage;

export type ChatActions = {
  setMessages: (updater: (messages: Message[]) => Message[]) => void;
  getMessages: () => Message[];
  setUserInput: (input: string) => void;
  setCurrentStep: (updater: (step: number) => number) => void;
  updateBrief: (field: string, value: string) => void;
  setSendButtonAnimation: (animate: boolean) => void;
  setShowHelpSuggestions: (show: boolean) => void;
  setShowCompletionButtons: (show: boolean) => void;
  setIsLoading?: (loading: boolean) => void;
  focusInput?: () => void;
  getAuthToken?: () => string | null;
  getAllowedTools?: () => string[];
  getSessionId?: () => string | undefined;
  getLanguage?: () => string;
  setSessionFromResponse?: (session: { id: string; name: string }) => void;
  setLastToolsCalls?: (calls: ChatCompletionResponse['lastToolsCalls']) => void;
  setTotalTokens?: (tokens: number) => void;
};

export const chatFlow: ChatFlowMessage[] = [
  {
    id: '0',
    type: 'system',
    //content: 'Welcome! I\'m here to answer all your questions. Let\'s get started!',
    //content: 'Welcome! I\'m Webby, your assistant, I\'m here to answer all your questions. Let\'s get started!',
    content: 'Hello! How can I help? I\'m Evo, your assistant. This message will not be included in your session. Please start a new session to begin your website creation journey!',
    isFinished: false
  }
];

export function getWritingSuggestions(currentStep: number): string[] {

  console.log('getWritingSuggestions :: currentStep', currentStep);

  if (currentStep < chatFlow.length) {
    const currentQuestion = chatFlow[currentStep];

    if (currentQuestion.suggestions) {
      return currentQuestion.suggestions;
    }

    switch (currentQuestion.field) {
      case 'businessName':
        return [
          'Use your actual business name',
          'Add a distinctive modifier',
          'Include your industry',
          'Keep it short and memorable'
        ];
      case 'businessType':
        return [
          'Health and wellness',
          'Nutritional supplements',
          'Direct-to-consumer vitamins',
          'Subscription vitamin service'
        ];
      case 'product':
        return [
          'List your flagship products',
          'Mention product categories',
          'Highlight specialty formulations',
          'Include subscription options'
        ];
      case 'targetAudience':
        return [
          'Health-conscious adults',
          'Athletes and fitness enthusiasts',
          'Seniors looking for wellness',
          'Parents seeking children\'s supplements'
        ];
      case 'serviceOffering':
        return [
          'Product catalog with filtering',
          'Personalized recommendations',
          'Educational content and blog',
          'Customer reviews and testimonials'
        ];
      case 'websiteGoal':
        return [
          'Drive online sales',
          'Educate visitors about nutrition',
          'Build subscription customer base',
          'Establish brand credibility'
        ];
      default:
        return [
          'I need more specific details',
          'Please explain in 1-2 sentences',
          'Could you give an example?',
          'Let me try a different approach'
        ];
    }
  }

  return [
    'I need more specific details',
    'Please explain in 1-2 sentences',
    'Could you give an example?',
    'Let me try a different approach'
  ];
}

// Utility function to simulate API calls with possible errors
const simulateApiCall = async (data: any, errorRate = 0.2): Promise<any> => {
  // Simulate API processing time
  await new Promise(resolve => setTimeout(resolve, 300));

  // Randomly fail based on errorRate (20% by default)
  if (Math.random() < errorRate) {
    throw new Error('Failed to process your response. The server is currently experiencing issues. Please try again.');
  }

  // Return processed data
  return data;
};

export const handleUserResponse = async (
  actions: ChatActions,
  currentStep: number,
  userInput: string,
  response: string | null = null
) => {

  console.log('handleUserResponse :: actions', actions);
  console.log('handleUserResponse :: currentStep', currentStep);
  console.log('handleUserResponse :: userInput', userInput);
  console.log('handleUserResponse :: response', response);

  // Get current messages
  const currentMessagesList = actions.getMessages();
  console.log('handleUserResponse :: currentMessagesList', currentMessagesList);

  // Get API requirements
  const authToken = actions.getAuthToken?.() || localStorage.getItem('authToken');

  if (!authToken) {
    console.error('No auth token available for API request');
    const errorMessage: ErrorMessage = {
      id: `error-auth-${Date.now()}`,
      type: 'error',
      content: 'Authentication error. Please log in and try again.',
      errorCode: 'AUTH_ERROR'
    };
    actions.setMessages((prev: Message[]) => [...prev, errorMessage]);
    return;
  }

  // Trigger send button animation
  actions.setSendButtonAnimation(true);
  setTimeout(() => actions.setSendButtonAnimation(false), 400);

  const currentQuestion = chatFlow[currentStep] as ChatFlowMessage | undefined;
  const userMessage: UserMessage = {
    id: `user-${currentStep}`,
    type: 'user',
    content: response || userInput
  };

  // for error displayed with "Try again" - If the user message already exists, add a retry suffix to the id
  if (currentMessagesList.find(message => message.id === userMessage.id)) {
    userMessage.id += `-retry-${Date.now()}`;
  }

  // Add user message to the chat
  actions.setMessages((prev: Message[]) => [...prev, userMessage]);

  try {
    // Set loading state to true before API call
    if (actions.setIsLoading) {
      actions.setIsLoading(true);
    }

    const inputData = response || userInput;
    const currentField = currentQuestion && currentQuestion.field ? currentQuestion.field : 'Last input';
    console.log(`Processing user input for field: ${currentField}`);

    // Prepare messages for the API
    const apiMessages: ChatMessage[] = currentMessagesList
      .filter(msg => msg.type === 'user' || msg.type === 'system' || msg.type === 'question')
      .map(msg => ({
        id: msg.id,
        role: msg.type === 'user' ? 'user' : 'assistant',
        content: msg.content
      }));

    // Add the current user message
    apiMessages.push({
      id: userMessage.id,
      role: userMessage.type === 'user' ? 'user' : 'assistant',
      content: userMessage.content
    });

    // Call the OpenAI API
    const allowedTools = actions.getAllowedTools ? actions.getAllowedTools() : [];
    let sessionId = actions.getSessionId ? actions.getSessionId() : undefined;
    if (!sessionId) {
      // Auto-create a new session
      const autoName = `Session started at ${new Date().toISOString()}`;
      const language = actions.getLanguage ? actions.getLanguage() : 'en';
      const newSession = await createSession(authToken, autoName, allowedTools, language);
      actions.setSessionFromResponse && actions.setSessionFromResponse(newSession);
      sessionId = newSession.id;
    }
    console.log('Sending to API:', { messages: apiMessages, allowedTools, sessionId });
    const apiResponse = await sendChatRequest(apiMessages, authToken, allowedTools, sessionId as string);

    // Handle API response
    if (apiResponse.status === 'Error') {
      throw new Error(apiResponse.lastReply.content || 'API request failed');
    }

    console.log('API response:', apiResponse);

    actions.setTotalTokens?.(apiResponse.totalTokens);

    // sessionId is required now; no need to set from response

    // Expose last tools calls to UI if a setter is provided
    if (actions.setLastToolsCalls) {
      actions.setLastToolsCalls(apiResponse.lastToolsCalls || []);
    }

    const replyContent = apiResponse.lastReply?.content ?? '';
    const rawSuggestions = (apiResponse.lastReply as { suggestions?: { options?: string[] } } | undefined)?.suggestions;
    const assistantMessageId = apiResponse.lastReply?.id || `assistant-${Date.now()}`;

    const assistantMessage: Message = {
      id: assistantMessageId,
      type: 'system',
      content: replyContent,
      timestamp: apiResponse.lastReply?.timestamp,
      duration: apiResponse.lastReply?.duration,
      isLiked: apiResponse.lastReply?.is_liked,
      isDisliked: apiResponse.lastReply?.is_disliked,
      role: apiResponse.lastReply?.role
    };

    actions.setMessages((prev: Message[]) => {
      const filtered = prev.filter(msg => msg.id !== assistantMessageId);
      return [...filtered, assistantMessage];
    });

    const suggestionsOptions = rawSuggestions?.options ?? [];
    const nextQuestion = chatFlow[currentStep + 1];
    const nextQuestionBase: ChatFlowMessage = {
      id: assistantMessageId,
      type: 'question',
      content: assistantMessage.content,
      suggestions: suggestionsOptions,
      isFinished: apiResponse.isConversationEnded,
      timestamp: assistantMessage.timestamp,
      duration: assistantMessage.duration,
      isLiked: assistantMessage.isLiked,
      isDisliked: assistantMessage.isDisliked,
      role: assistantMessage.role
    };

    if (nextQuestion) {
      chatFlow[currentStep + 1] = {
        ...nextQuestionBase,
        field: nextQuestion.field
      };
    } else {
      chatFlow.push(nextQuestionBase);
    }

    // Update brief data with processed response
    actions.updateBrief(currentField, inputData);

  } catch (error) {
    console.error('Error processing user response:', error);

    // Create an error message to display to the user
    const errorMessage: ErrorMessage = {
      id: `error-${Date.now()}`,
      type: 'error',
      content: error instanceof Error
        ? error.message
        : 'Something went wrong. Please try again.',
      errorCode: 'API_ERROR'
    };

    // Add the error message to the chat
    actions.setMessages((prev: Message[]) => [...prev, errorMessage]);

    // Don't proceed to the next step when there's an error
    return;

  } finally {
    // Set loading state to false after API call completes (success or error)
    if (actions.setIsLoading) {
      actions.setIsLoading(false);
    }
  }

  // Move to next step
  actions.setCurrentStep((prev: number) => prev + 1);
  actions.setUserInput('');
  actions.setShowHelpSuggestions(false); // Hide help suggestions after submitting
};

export const handleSkipQuestion = (
  actions: ChatActions
) => {

  console.log('handleSkipQuestion :: actions', actions);

  actions.setCurrentStep((prev: number) => prev + 1);
  actions.setUserInput('');
  actions.setShowHelpSuggestions(false);
};

export const handleHelpClick = (
  actions: ChatActions,
  currentStep: number
) => {

  console.log('handleHelpClick :: actions', actions);
  console.log('handleHelpClick :: currentStep', currentStep);

  // Create unique timestamp for this help request
  const timestamp = Date.now();

  // Add user message asking for help with a unique ID
  const userHelpMessage: UserMessage = {
    id: `user-help-${currentStep}-${timestamp}`,
    type: 'user',
    content: 'Offer me some suggestions for what I can write'
  };

  // Add AI response with suggestions with a unique ID
  const aiHelpMessage: HelpMessage = {
    id: `ai-help-${currentStep}-${timestamp}`,
    type: 'help',
    content: `Here are some ideas for answering the current question:`,
    suggestions: getWritingSuggestions(currentStep)
  };

  actions.setMessages((prev: Message[]) => [...prev, userHelpMessage, aiHelpMessage]);
  actions.setShowHelpSuggestions(true);
};

export const handleHelpSuggestionClick = (
  actions: ChatActions,
  suggestion: string
) => {

  console.log('handleHelpSuggestionClick :: actions', actions);
  console.log('handleHelpSuggestionClick :: suggestion', suggestion);

  actions.setUserInput(suggestion);
  if (actions.focusInput) {
    actions.focusInput();
  }
};

export const handleProceed = (
  actions: ChatActions
) => {

  console.log('handleProceed :: actions', actions);

  // Add final confirmation message from AI
  const proceedMessage: ProceedMessage = {
    id: 'proceed-confirmation',
    type: 'system',
    content: "Perfect! I'm generating your website now. This will just take a moment..."
  };

  actions.setMessages((prev: Message[]) => [...prev, proceedMessage]);
  actions.setShowCompletionButtons(false);

  // Here you would typically start the website generation process
  // For now we'll just show a confirmation message
};

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' bytes';
  else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  else return (bytes / 1048576).toFixed(1) + ' MB';
};

export const handleSendFile = (
  actions: ChatActions,
  selectedFile: File,
  setSelectedFile: (file: File | null) => void
) => {

  console.log('handleSendFile :: actions', actions);
  console.log('handleSendFile :: selectedFile', selectedFile);
  console.log('handleSendFile :: setSelectedFile', setSelectedFile);

  if (!selectedFile) return;

  // Add a message with the file
  const fileMessage: UserMessage = {
    id: `file-upload-${Date.now()}`,
    type: 'user',
    content: `Uploaded file: ${selectedFile.name}`,
    file: {
      name: selectedFile.name,
      type: selectedFile.type,
      size: formatFileSize(selectedFile.size)
    }
  };

  actions.setMessages((prev: Message[]) => [...prev, fileMessage]);

  // Clear the selected file
  setSelectedFile(null);

  // Simulate AI response to file upload
  setTimeout(() => {
    const aiResponse: ProceedMessage = {
      id: `ai-file-response-${Date.now()}`,
      type: 'system',
      content: `I've received your file "${selectedFile.name}". Would you like me to analyze its contents or use it as part of your brief?`
    };

    actions.setMessages((prev: Message[]) => [...prev, aiResponse]);
  }, 1000);
};
